import React, { useState, useMemo, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Calculator, 
  TrendingUp, 
  ShieldAlert, 
  CheckCircle2, 
  ArrowRight, 
  DollarSign, 
  Zap, 
  RotateCcw, 
  ChevronDown, 
  Building2, 
  Package,
  Wrench,
  Users,
  Info,
  Layers,
  Briefcase,
  ExternalLink
} from 'lucide-react';
import logoRmaxWebp from '../assets/logo-rmax.webp';
import logoRmaxJpeg from '../assets/logo-rmax.jpeg';
import logo4Shift from '../assets/4shift-logo.png';
import { CatalogAuditLeadModal } from '../components/CatalogAuditLeadModal';
import { TaxDiagnosticIntroModal } from '../components/TaxDiagnosticIntroModal';
import { PostCalculationActionHub } from '../components/PostCalculationActionHub';

const FAQS_DATA = [
  {
    q: 'O que muda no cálculo de preços com a nova Reforma Tributária (CBS e IBS)?',
    a: 'No sistema atual, os impostos são calculados "por dentro" (embutidos na própria base do preço via fórmula do divisor). Na Reforma Tributária, o IVA Dual (CBS federal + IBS estadual/municipal) é calculado "por fora": apura-se primeiro o preço de venda líquido da empresa e soma-se o imposto sobre ele. Além disso, todo imposto pago na compra de insumos gera crédito fiscal que diminui seu preço de custo real.'
  },
  {
    q: 'Qual a diferença entre precificar Produto vs Serviço na Reforma?',
    a: 'Quem vende Produto possui cadeia longa de insumos (matéria-prima, embalagem, frete), gerando créditos fiscais que reduzem o custo de compra. Já quem presta Serviço tem a mão de obra como custo principal (que não gera crédito de IBS/CBS). Por isso, a lei concedeu alíquotas com desconto: 60% de redução para Saúde e Educação (~10,6%), e 30% de redução para Profissionais Liberais regulamentados como médicos, veterinários, engenheiros e advogados (~18,5%).'
  },
  {
    q: 'Por que a venda B2B (para empresas) fica mais competitiva ao destacar imposto por fora?',
    a: 'O cliente empresa (Pessoa Jurídica) abate 100% do imposto destacado na sua nota como crédito tributário no caixa dele. Se sua nota for de R$ 1.000,00 com R$ 265,00 de IBS/CBS, o custo real que seu cliente PJ desembolsa é de apenas R$ 735,00. Ensinar esse benefício ao seu comprador corporativo protege sua margem de lucro e acelera o fechamento de propostas comerciais.'
  },
  {
    q: 'Por que não posso apenas somar minha margem de lucro em cima do custo?',
    a: 'Se você gasta R$ 50 para fazer um produto e soma 30%, você vende por R$ 65. Só que quando você passa na maquininha do cartão ou emite nota fiscal, essas taxas incidem sobre os R$ 65 que o cliente pagou, e não sobre os R$ 50. No final do mês, você acha que lucrou 30%, mas na realidade lucrou muito menos ou ficou no zero a zero. O método do R-Max calcula o preço já blindando as taxas.'
  },
  {
    q: 'Sou MEI, preciso me preocupar com impostos por venda?',
    a: 'O MEI paga um valor fixo mensal no boleto DAS (não paga imposto por produto emitido). Por isso, na nossa calculadora você pode deixar 0% de imposto por venda, focando apenas nas taxas de maquininha e no custo do produto.'
  },
  {
    q: 'Como o R-Max me ajuda com as contas de aluguel e custos fixos?',
    a: 'No aplicativo completo do R-Max, você só cadastra suas contas mensais (aluguel, água, internet, salários). O sistema analisa sua capacidade de produção e divide automaticamente esses custos entre todos os itens do seu cardápio ou estoque, sem você precisar quebrar a cabeça com fórmulas matemáticas.'
  },
  {
    q: 'Consigo cadastrar receitas e fichas técnicas no R-Max?',
    a: 'Sim! Você cadastra o quilo da carne, o pacote de queijo, o óleo e a embalagem. O R-Max calcula o custo exato de cada porção com perda operacional e atualiza todos os seus preços de venda automaticamente quando o fornecedor aumentar o valor de qualquer ingrediente.'
  }
];

interface PublicCalculatorPageProps {
  defaultTab?: 'quick' | 'reforma' | 'audit';
}

export default function PublicCalculatorPage({ defaultTab }: PublicCalculatorPageProps = {}) {
  const location = useLocation();

  // Modo da Calculadora: 3 Abas Dedicadas (Opção A)
  // 'quick': Preço Rápido (Dia a dia, limpo, 3 passos)
  // 'reforma': Simulador Reforma Tributária (CBS/IBS, créditos de insumo, B2B e Comparativo)
  // 'audit': Auditoria de Preço Atual ("Ver se estou lucrando")
  const [activeTab, setActiveTab] = useState<'quick' | 'reforma' | 'audit'>(() => {
    if (typeof window !== 'undefined' && window.location.pathname.includes('reforma-tributaria')) {
      return 'reforma';
    }
    const params = new URLSearchParams(window.location.search);
    if (params.get('modo') === 'reforma') return 'reforma';
    if (params.get('modo') === 'audit') return 'audit';
    if (defaultTab) return defaultTab;
    return 'quick';
  });

  // Atualizar aba se a rota ou query params mudarem
  useEffect(() => {
    if (location.pathname.includes('reforma-tributaria')) {
      setActiveTab('reforma');
      return;
    }
    const params = new URLSearchParams(location.search);
    const mode = params.get('modo');
    if (mode === 'reforma') {
      setActiveTab('reforma');
    } else if (mode === 'audit') {
      setActiveTab('audit');
    } else if (mode === 'quick') {
      setActiveTab('quick');
    } else if (defaultTab && !params.has('modo')) {
      setActiveTab(defaultTab);
    }
  }, [location.pathname, location.search, defaultTab]);

  // Manipulador de troca de aba com sincronização de URL e histórico do navegador
  const handleTabChange = (tab: 'quick' | 'reforma' | 'audit') => {
    setActiveTab(tab);
    let targetUrl = location.pathname;
    const isCurrentlyReformaPath = location.pathname.includes('reforma-tributaria');

    if (tab === 'reforma') {
      targetUrl = isCurrentlyReformaPath ? location.pathname : '/calculadora-reforma-tributaria';
    } else if (tab === 'quick') {
      targetUrl = isCurrentlyReformaPath ? '/calculadora-preco-venda' : location.pathname;
      if (location.search.includes('modo=')) {
        targetUrl = location.pathname;
      }
    } else if (tab === 'audit') {
      targetUrl = isCurrentlyReformaPath ? '/calculadora-preco-venda?modo=audit' : `${location.pathname}?modo=audit`;
    }

    if (window.location.pathname + window.location.search !== targetUrl) {
      window.history.pushState(null, '', targetUrl);
    }
  };

  // Injeção de Meta Tags e SEO para o Googlebot (Canonical e Schema FAQPage corretos)
  useEffect(() => {
    const isReforma = location.pathname.includes('reforma-tributaria') || activeTab === 'reforma';
    const isMarkupRoute = location.pathname.includes('calculadora-markup');

    let pageTitle = 'Calculadora de Preço de Venda Grátis: Calcule Markup e Lucro Real | RESOLV MAX';
    let pageDescription = 'Calcule o preço de venda ideal do seu produto em segundos. Descubra sua margem de lucro real descontando taxas de cartão, maquininha, iFood e custos. Ferramenta 100% grátis.';
    let canonicalPath = '/calculadora-preco-venda';

    if (isReforma) {
      pageTitle = 'Calculadora da Reforma Tributária 2026: Simulador CBS, IBS e IVA Dual | RESOLV MAX';
      pageDescription = 'Simule o impacto da Reforma Tributária (CBS e IBS) no seu preço de venda. Calcule o imposto por fora, créditos na compra de insumos e competitividade B2B. Ferramenta 100% grátis.';
      canonicalPath = '/calculadora-reforma-tributaria';
    } else if (isMarkupRoute) {
      pageTitle = 'Calculadora de Markup Grátis: Calcule o Índice Multiplicador e Divisor | RESOLV MAX';
      pageDescription = 'Calcule o markup divisor e multiplicador dos seus produtos em segundos. Proteja sua margem de lucro real descontando taxas de maquininha, iFood e impostos. Ferramenta 100% grátis.';
      canonicalPath = '/calculadora-markup';
    }

    const fullCanonicalUrl = `https://precificacao.resolvmax.com.br${canonicalPath}`;

    const originalTitle = document.title;
    document.title = pageTitle;

    // Meta Description
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    const prevDesc = metaDesc ? metaDesc.content : '';
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = pageDescription;

    // Canonical Link Oficial
    let linkCanonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    const prevCanonical = linkCanonical ? linkCanonical.href : '';
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.rel = 'canonical';
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.href = fullCanonicalUrl;

    // OpenGraph Tags
    const updateOg = (property: string, content: string) => {
      let og = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!og) {
        og = document.createElement('meta');
        og.setAttribute('property', property);
        document.head.appendChild(og);
      }
      og.content = content;
    };
    updateOg('og:title', pageTitle);
    updateOg('og:description', pageDescription);
    updateOg('og:url', fullCanonicalUrl);

    // Schema.org JSON-LD Combinado: WebApplication + FAQPage
    const scriptId = 'rmax-calculator-jsonld';
    let scriptTag = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.id = scriptId;
      scriptTag.type = 'application/ld+json';
      document.head.appendChild(scriptTag);
    }
    const appName = isReforma
      ? 'Calculadora da Reforma Tributária 2026 (CBS, IBS e IVA Dual)'
      : isMarkupRoute
        ? 'Calculadora de Markup Grátis'
        : 'Calculadora de Preço de Venda e Markup Grátis';

    scriptTag.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebApplication',
          'name': appName,
          'applicationCategory': 'BusinessApplication',
          'operatingSystem': 'All',
          'url': fullCanonicalUrl,
          'description': pageDescription,
          'offers': {
            '@type': 'Offer',
            'price': '0',
            'priceCurrency': 'BRL'
          },
          'publisher': {
            '@type': 'Organization',
            'name': 'RESOLV MAX',
            'url': 'https://www.resolvmax.com.br'
          }
        },
        {
          '@type': 'FAQPage',
          'mainEntity': FAQS_DATA.map(item => ({
            '@type': 'Question',
            'name': item.q,
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': item.a
            }
          }))
        }
      ]
    });

    return () => {
      document.title = originalTitle;
      if (metaDesc) metaDesc.content = prevDesc;
      if (linkCanonical) linkCanonical.href = prevCanonical;
      const scriptToRemove = document.getElementById(scriptId);
      if (scriptToRemove) scriptToRemove.remove();
    };
  }, [location.pathname, activeTab]);

  // --- ESTADOS DO FORMULÁRIO ---
  const [costInput, setCostInput] = useState<string>('30');
  const [profitChoice, setProfitChoice] = useState<number>(30); // 15, 20, 30, 40 ou 0 para custom
  const [customProfitInput, setCustomProfitInput] = useState<string>('30');

  // Canal de venda pré-configurado
  const [channelChoice, setChannelChoice] = useState<string>('credit');
  const [customFeeInput, setCustomFeeInput] = useState<string>('3.5');

  // Imposto no Sistema Atual (MEI ou Simples)
  const [taxChoice, setTaxChoice] = useState<string>('mei');
  const [customTaxInput, setCustomTaxInput] = useState<string>('6');

  // Custos fixos (Opcional)
  const [includeFixed, setIncludeFixed] = useState<boolean>(false);
  const [monthlyFixedBills, setMonthlyFixedBills] = useState<string>('3000');
  const [monthlyRevenue, setMonthlyRevenue] = useState<string>('15000');

  // Chaves Mestras da Reforma Tributária (Alinhamento Matriz 4Shift)
  const [purchaseScenario, setPurchaseScenario] = useState<'full_rate' | 'transition_legacy'>('full_rate');
  const [companyTaxRegime, setCompanyTaxRegime] = useState<'simples_hibrido' | 'simples_normal' | 'lucro_real_presumido'>('simples_hibrido');
  const [activityType, setActivityType] = useState<'product' | 'service'>('product');
  const [serviceCategory, setServiceCategory] = useState<'general' | 'health_edu' | 'liberal_pro'>('general');
  const [customerType, setCustomerType] = useState<'b2c' | 'b2b' | 'governo'>('b2c');
  const [taxBenchmark, setTaxBenchmark] = useState<'tcu_2026' | 'fazenda_preliminar_8_8'>('tcu_2026');
  const [serviceMaterialInput, setServiceMaterialInput] = useState<string>('0');
  const [serviceLaborInput, setServiceLaborInput] = useState<string>('30');

  // Crédito de Insumos na Reforma Tributária
  const [includeInputCredit, setIncludeInputCredit] = useState<boolean>(true);
  const [inputMaterialCreditPct] = useState<string>('26.5');

  // MODO AUDITORIA: Auditoria de Preço Atual
  const [auditCost, setAuditCost] = useState<string>('30');
  const [auditPrice, setAuditPrice] = useState<string>('50');
  const [auditChannel, setAuditChannel] = useState<string>('credit');
  const [auditCustomFee, setAuditCustomFee] = useState<string>('3.5');
  const [auditTaxChoice, setAuditTaxChoice] = useState<string>('mei');
  const [auditCustomTax, setAuditCustomTax] = useState<string>('6');

  // FAQ Accordion
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  // Modais de Captação B2B e Diagnóstico Tributário
  const [isCatalogModalOpen, setIsCatalogModalOpen] = useState(false);
  const [isTaxDiagnosticModalOpen, setIsTaxDiagnosticModalOpen] = useState(false);
  const [catalogModalOrigin, setCatalogModalOrigin] = useState<'quick' | 'reforma' | 'audit'>('quick');

  const handleOpenCatalogModal = (origin: 'quick' | 'reforma' | 'audit') => {
    setCatalogModalOrigin(origin);
    setIsCatalogModalOpen(true);
  };

  const handleOpenTaxDiagnosticModal = (origin: 'quick' | 'reforma' | 'audit') => {
    setCatalogModalOrigin(origin);
    setIsTaxDiagnosticModalOpen(true);
  };

  const parseNum = (val: string) => {
    const clean = val.replace(',', '.');
    const n = parseFloat(clean);
    return isNaN(n) ? 0 : Math.max(0, n);
  };

  // Taxa de canal selecionada
  const activeFeePct = useMemo(() => {
    switch (channelChoice) {
      case 'pix': return 0;
      case 'debit': return 1.5;
      case 'credit': return 3.5;
      case 'credit_split': return 7.0;
      case 'ifood': return 18.0;
      case 'mktplace': return 14.0;
      case 'custom': return parseNum(customFeeInput);
      default: return 3.5;
    }
  }, [channelChoice, customFeeInput]);

  // Imposto selecionado (Sistema Atual)
  const activeTaxPct = useMemo(() => {
    switch (taxChoice) {
      case 'mei': return 0;
      case 'simples': return 6.0;
      case 'custom': return parseNum(customTaxInput);
      default: return 0;
    }
  }, [taxChoice, customTaxInput]);

  // Alíquota IVA Reforma Tributária (CBS + IBS) com Benchmarks Oficiais
  const reformaIvaPct = useMemo(() => {
    const isTcu = taxBenchmark === 'tcu_2026';
    const standardCombined = isTcu ? 28.0 : 26.5; // TCU 11,3% CBS + 16,7% IBS vs Fazenda 8,8% CBS + 17,7% IBS

    let baseRate = standardCombined;
    if (activityType === 'service') {
      if (serviceCategory === 'health_edu') {
        baseRate = Number((standardCombined * 0.40).toFixed(2)); // 60% de redução legal
      } else if (serviceCategory === 'liberal_pro') {
        baseRate = Number((standardCombined * 0.70).toFixed(2)); // 30% de redução legal
      }
    }

    // Redutor Governamental para Compras Públicas (LC 214/2025 art. 349 e Resolução-TCU 388/2026: 44,2%)
    if (customerType === 'governo') {
      baseRate = Number((baseRate * (1 - 0.442)).toFixed(2));
    }

    return baseRate;
  }, [activityType, serviceCategory, taxBenchmark, customerType]);

  // Margem de lucro desejada
  const activeProfitPct = useMemo(() => {
    return profitChoice === 0 ? parseNum(customProfitInput) : profitChoice;
  }, [profitChoice, customProfitInput]);

  // Porcentagem de custo fixo (se ativado)
  const activeFixedPct = useMemo(() => {
    if (!includeFixed) return 0;
    const bills = parseNum(monthlyFixedBills);
    const rev = parseNum(monthlyRevenue);
    if (rev <= 0 || bills <= 0) return 0;
    return (bills / rev) * 100;
  }, [includeFixed, monthlyFixedBills, monthlyRevenue]);

  // CÁLCULO GERAL (SISTEMA ATUAL + REFORMA TRIBUTÁRIA INTEGRADOS - ALINHAMENTO CONTÁBIL)
  const idealCalc = useMemo(() => {
    const rawCost = parseNum(costInput);
    const fees = activeFeePct;
    const fixed = activeFixedPct;
    const desiredProfit = activeProfitPct;

    // --- 1. MODELO REFORMA TRIBUTÁRIA (IVA DUAL / POR FORA - PLANILHAS MATRIZ 4SHIFT & TCU 2026) ---
    const isTcu = taxBenchmark === 'tcu_2026';
    const cbsRate = isTcu ? 11.3 : 8.8;
    const ibsRate = isTcu ? 16.7 : 17.7;
    const effectiveSaleIvaPct = reformaIvaPct;

    let effectiveRawCost = rawCost;
    let serviceMaterials = 0;
    let serviceLabor = 0;

    if (activityType === 'service') {
      serviceMaterials = parseNum(serviceMaterialInput);
      serviceLabor = parseNum(serviceLaborInput);
      if (serviceMaterials > 0 || serviceLabor > 0) {
        effectiveRawCost = serviceMaterials + serviceLabor;
      }
    }

    // Compra e Formação do Custo de Entrada
    let supplierNetBase = effectiveRawCost;
    let purchaseCbsVal = 0;
    let purchaseIbsVal = 0;
    let purchaseNfTotal = effectiveRawCost;
    let purchaseCreditCbs = 0;
    let purchaseCreditIbs = 0;
    let totalPurchaseCredits = 0;
    let inventoryNetCost = effectiveRawCost;

    if (activityType === 'service' && effectiveRawCost > 0) {
      // LEI DA ASSIMETRIA: Mão de obra tem crédito ZERO; apenas insumos físicos tomam crédito
      if (serviceMaterials > 0) {
        purchaseCbsVal = (serviceMaterials * cbsRate) / 100;
        purchaseIbsVal = (serviceMaterials * ibsRate) / 100;
        purchaseNfTotal = serviceLabor + serviceMaterials + purchaseCbsVal + purchaseIbsVal;

        if (companyTaxRegime === 'simples_normal' || !includeInputCredit) {
          purchaseCreditCbs = 0;
          purchaseCreditIbs = 0;
          totalPurchaseCredits = 0;
          inventoryNetCost = purchaseNfTotal;
        } else {
          purchaseCreditCbs = purchaseCbsVal;
          purchaseCreditIbs = purchaseIbsVal;
          totalPurchaseCredits = purchaseCreditCbs + purchaseCreditIbs;
          inventoryNetCost = serviceLabor + serviceMaterials;
        }
      } else {
        inventoryNetCost = serviceLabor;
        purchaseNfTotal = serviceLabor;
      }
      supplierNetBase = serviceLabor + serviceMaterials;
    } else if (activityType === 'product' && rawCost > 0) {
      if (purchaseScenario === 'full_rate') {
        // Cenário Alíquota Cheia (Coluna 4 da planilha do Ronny):
        // Fornecedor cobra preço base líquido (R$ 100) e soma 26.5% por fora (CBS 8.80 + IBS 17.70) = NF R$ 126.50
        supplierNetBase = rawCost;
        purchaseCbsVal = (supplierNetBase * cbsRate) / 100;
        purchaseIbsVal = (supplierNetBase * ibsRate) / 100;
        purchaseNfTotal = supplierNetBase + purchaseCbsVal + purchaseIbsVal;

        if (companyTaxRegime === 'simples_normal' || !includeInputCredit) {
          // Simples Normal NÃO apropria créditos de IBS/CBS
          purchaseCreditCbs = 0;
          purchaseCreditIbs = 0;
          totalPurchaseCredits = 0;
          inventoryNetCost = purchaseNfTotal; // Custo do estoque fica em R$ 126,50
        } else {
          // Simples Híbrido ou Lucro Real/Presumido apropria créditos integrais
          purchaseCreditCbs = purchaseCbsVal;
          purchaseCreditIbs = purchaseIbsVal;
          totalPurchaseCredits = purchaseCreditCbs + purchaseCreditIbs;
          inventoryNetCost = supplierNetBase; // Custo de estoque fica em R$ 100,00!
        }
      } else {
        // Cenário Transição / Desoneração (Colunas 1 a 3 da planilha do Ronny):
        // Compra atual com NF de R$ 100 desonera 27.25% de ICMS (18%) + PIS (1.65%) + COFINS (7.60%) -> base R$ 72.75
        // Sobre os 72.75 incidem CBS (6.40) + IBS (12.88) = NF R$ 92.03
        const legacyTaxesVal = rawCost * 0.2725;
        supplierNetBase = Math.max(0, rawCost - legacyTaxesVal);
        purchaseCbsVal = (supplierNetBase * cbsRate) / 100;
        purchaseIbsVal = (supplierNetBase * ibsRate) / 100;
        purchaseNfTotal = supplierNetBase + purchaseCbsVal + purchaseIbsVal;

        if (companyTaxRegime === 'simples_normal' || !includeInputCredit) {
          purchaseCreditCbs = 0;
          purchaseCreditIbs = 0;
          totalPurchaseCredits = 0;
          inventoryNetCost = purchaseNfTotal; // R$ 92,03 no Simples normal
        } else {
          purchaseCreditCbs = purchaseCbsVal;
          purchaseCreditIbs = purchaseIbsVal;
          totalPurchaseCredits = purchaseCreditCbs + purchaseCreditIbs;
          inventoryNetCost = supplierNetBase; // R$ 72,75 no Simples Híbrido e Lucro Real
        }
      }
    }

    // Formação do Preço de Venda
    const reformaCostBase = inventoryNetCost;
    const reformaBaseDeductions = fees + fixed + desiredProfit;
    const reformaHasError = reformaBaseDeductions >= 100;
    const reformaDivisor = (100 - reformaBaseDeductions) / 100;
    const netSellingPrice = reformaDivisor > 0 && reformaCostBase > 0 ? reformaCostBase / reformaDivisor : 0;

    // CBS + IBS destacado na nota fiscal de saída (por fora)
    let reformaIvaVal = 0;
    let b2bCreditTransferred = 0;

    if (companyTaxRegime === 'simples_normal') {
      // No Simples Normal, a empresa paga DAS sobre a receita e não destaca IBS/CBS por fora
      const simplesOutTaxRate = activeTaxPct || 6;
      reformaIvaVal = Number(((netSellingPrice * simplesOutTaxRate) / 100).toFixed(2));
      b2bCreditTransferred = 0; // Não transfere crédito pleno para clientes PJ
    } else {
      // No Simples Híbrido ou Lucro Real, destaca CBS + IBS integral por fora
      reformaIvaVal = Number(((netSellingPrice * effectiveSaleIvaPct) / 100).toFixed(2));
      b2bCreditTransferred = reformaIvaVal; // Transfere 100% para o cliente empresa (B2B)
    }

    // No Simulador da Reforma Tributária (Opção 1):
    // O preço final da nota fiscal é a soma exata do Subtotal da Empresa + Imposto Destacado (sem distorção de arredondamento comercial),
    // garantindo que a soma dos itens feche 100% matematicamente com a nota fiscal e a apuração fiscal.
    const roundedNetSellingPrice = Number(netSellingPrice.toFixed(2));
    const reformaTotalGross = Number((roundedNetSellingPrice + reformaIvaVal).toFixed(2));
    const reformaSuggestedPrice = reformaTotalGross;

    const reformaFeesVal = Number(((reformaSuggestedPrice * fees) / 100).toFixed(2));
    const reformaFixedVal = Number(((reformaSuggestedPrice * fixed) / 100).toFixed(2));
    const reformaProfitVal = Number(((netSellingPrice * desiredProfit) / 100).toFixed(2));
    const reformaMarkupMultiplier = rawCost > 0 ? reformaSuggestedPrice / rawCost : 0;

    const b2bEffectiveCostForClient = Number(Math.max(0, reformaSuggestedPrice - b2bCreditTransferred).toFixed(2));

    // Apuração do Imposto a Pagar ao Governo (Não-Cumulativo: Débito na Venda - Crédito na Compra)
    const reformaNetTaxToPay = companyTaxRegime === 'simples_normal' 
      ? reformaIvaVal 
      : Number(Math.max(0, reformaIvaVal - totalPurchaseCredits).toFixed(2));

    // Tabela dos 4 Cenários do Ronny (Colunas 1 a 4 da Planilha) baseada no custo informado
    const refCost = rawCost > 0 ? rawCost : 100;
    const colLegacyNetBase = refCost * 0.7275;
    const colTransCbs = colLegacyNetBase * 0.088;
    const colTransIbs = colLegacyNetBase * 0.177;
    const colTransNf = colLegacyNetBase + colTransCbs + colTransIbs; // R$ 92,03
    const colTransCredits = colTransCbs + colTransIbs; // R$ 19,28

    const colFullRateCbs = refCost * 0.088;
    const colFullRateIbs = refCost * 0.177;
    const colFullRateNf = refCost + colFullRateCbs + colFullRateIbs; // R$ 126,50
    const colFullRateCredits = colFullRateCbs + colFullRateIbs; // R$ 26,50

    const ronnyComparative = {
      lucroRealHoje: {
        credita: true,
        baseLiquida: colLegacyNetBase,
        cbs: 0,
        ibs: 0,
        totalNf: refCost,
        creditos: refCost * 0.2725,
        custoEstoque: colLegacyNetBase,
      },
      simplesNormal: {
        credita: false,
        baseLiquida: colLegacyNetBase,
        cbs: colTransCbs,
        ibs: colTransIbs,
        totalNf: colTransNf,
        creditos: 0,
        custoEstoque: colTransNf, // R$ 92,03
      },
      simplesHibrido: {
        credita: true,
        baseLiquida: colLegacyNetBase,
        cbs: colTransCbs,
        ibs: colTransIbs,
        totalNf: colTransNf,
        creditos: colTransCredits,
        custoEstoque: colLegacyNetBase, // R$ 72,75
      },
      lucroRealPosReforma: {
        credita: true,
        baseLiquida: refCost,
        cbs: colFullRateCbs,
        ibs: colFullRateIbs,
        totalNf: colFullRateNf,
        creditos: colFullRateCredits,
        custoEstoque: refCost, // R$ 100,00
      }
    };

    // --- 2. MODELO ATUAL (POR DENTRO / SIMPLES / MEI) ---
    const currentTax = activeTaxPct;
    const currentTotalDeductions = currentTax + fees + fixed + desiredProfit;
    const currentHasError = currentTotalDeductions >= 100;
    const currentDivisor = (100 - currentTotalDeductions) / 100;
    const currentRawSuggested = currentDivisor > 0 && rawCost > 0 ? rawCost / currentDivisor : 0;
    // Artigo 2: Arredondamento obrigatório Math.ceil(valor / 5) * 5
    const currentSuggestedPrice = Math.max(0, Math.ceil(currentRawSuggested / 5) * 5);
    const currentTaxVal = (currentSuggestedPrice * currentTax) / 100;
    const currentFeesVal = (currentSuggestedPrice * fees) / 100;
    const currentFixedVal = (currentSuggestedPrice * fixed) / 100;
    const currentProfitVal = (currentSuggestedPrice * desiredProfit) / 100;
    const currentMarkupMultiplier = rawCost > 0 ? currentSuggestedPrice / rawCost : 0;

    const isReforma = activeTab === 'reforma';
    const hasError = isReforma ? reformaHasError : currentHasError;
    const errorMsg = 'A soma das porcentagens (taxas + margem de lucro) atingiu 100% ou mais. Reduza a margem desejada para viabilizar a conta.';

    return {
      hasError,
      errorMsg,
      isReforma,
      cbsRate,
      ibsRate,
      activityType,
      serviceCategory,
      customerType,
      purchaseScenario,
      companyTaxRegime,
      rawCost,
      supplierNetBase,
      purchaseCbsVal,
      purchaseIbsVal,
      purchaseNfTotal,
      purchaseCreditCbs,
      purchaseCreditIbs,
      totalPurchaseCredits,
      inventoryNetCost,
      ronnyComparative,
      effectiveCost: isReforma ? inventoryNetCost : rawCost,
      suggestedPrice: isReforma ? reformaSuggestedPrice : currentSuggestedPrice,
      markupMultiplier: isReforma ? reformaMarkupMultiplier : currentMarkupMultiplier,
      taxVal: isReforma ? reformaIvaVal : currentTaxVal,
      feesVal: isReforma ? reformaFeesVal : currentFeesVal,
      fixedVal: isReforma ? reformaFixedVal : currentFixedVal,
      profitVal: isReforma ? reformaProfitVal : currentProfitVal,
      taxPct: isReforma ? (companyTaxRegime === 'simples_normal' ? (activeTaxPct || 6) : effectiveSaleIvaPct) : currentTax,
      feesPct: fees,
      fixedPct: fixed,
      profitPct: desiredProfit,
      netSellingPrice: isReforma ? roundedNetSellingPrice : netSellingPrice,
      inputCreditVal: totalPurchaseCredits,
      inputCreditPct: 26.5,
      b2bCreditTransferred,
      b2bEffectiveCostForClient,
      reformaNetTaxToPay,
      comparison: {
        currentPrice: currentSuggestedPrice,
        reformaPrice: reformaSuggestedPrice,
        priceDiff: reformaSuggestedPrice - currentSuggestedPrice,
        currentTaxVal,
        reformaTaxVal: reformaIvaVal,
        currentProfitVal,
        reformaProfitVal
      }
    };
  }, [
    costInput, 
    activeTab,
    activityType, 
    serviceCategory, 
    customerType, 
    purchaseScenario,
    companyTaxRegime,
    includeInputCredit, 
    reformaIvaPct, 
    activeTaxPct, 
    activeFeePct, 
    activeFixedPct, 
    activeProfitPct
  ]);

  // Auditoria de Preço
  const auditFeePct = useMemo(() => {
    switch (auditChannel) {
      case 'pix': return 0;
      case 'debit': return 1.5;
      case 'credit': return 3.5;
      case 'credit_split': return 7.0;
      case 'ifood': return 18.0;
      case 'mktplace': return 14.0;
      case 'custom': return parseNum(auditCustomFee);
      default: return 3.5;
    }
  }, [auditChannel, auditCustomFee]);

  const auditTaxPct = useMemo(() => {
    switch (auditTaxChoice) {
      case 'mei': return 0;
      case 'simples': return 6.0;
      case 'custom': return parseNum(auditCustomTax);
      default: return 0;
    }
  }, [auditTaxChoice, auditCustomTax]);

  const auditCalc = useMemo(() => {
    const cost = parseNum(auditCost);
    const price = parseNum(auditPrice);
    const feeVal = (price * auditFeePct) / 100;
    const taxVal = (price * auditTaxPct) / 100;

    const totalCosts = cost + feeVal + taxVal;
    const netProfit = price - totalCosts;
    const netMarginPct = price > 0 ? (netProfit / price) * 100 : 0;
    const markup = cost > 0 ? price / cost : 0;

    return {
      cost,
      price,
      feeVal,
      taxVal,
      totalCosts,
      netProfit,
      netMarginPct,
      markup,
      isLoss: netProfit < 0
    };
  }, [auditCost, auditPrice, auditFeePct, auditTaxPct]);

  const formatBRL = (val: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
  };

  const formatPct = (val: number) => {
    return val.toFixed(1).replace('.', ',') + '%';
  };

  return (
    <div className="min-h-screen bg-linear-to-b from-slate-50 via-white to-slate-100 text-slate-800 flex flex-col font-sans selection:bg-emerald-100 selection:text-emerald-900">
      {/* Header Público em Tema Claro */}
      <header className="border-b border-slate-200 bg-white/90 backdrop-blur sticky top-0 z-40 shadow-xs">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-18 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group focus:outline-none">
            <picture className="w-9 h-9 flex items-center justify-center">
              <source srcSet={logoRmaxWebp} type="image/webp" />
              <img 
                src={logoRmaxJpeg} 
                alt="R-Max Precificação" 
                className="w-9 h-9 object-contain rounded-lg border border-slate-200 shadow-xs group-hover:scale-105 transition-transform" 
              />
            </picture>
            <div className="flex flex-col">
              <span className="text-lg font-black tracking-tight text-slate-900 flex items-center gap-2">
                RESOLV MAX <span className="text-emerald-700 text-xs font-bold px-2.5 py-0.5 rounded-full bg-emerald-50 border border-emerald-200">Ferramenta Grátis</span>
              </span>
              <span className="text-[11px] text-slate-500 tracking-wider uppercase font-semibold">Calculadora Simplificada de Preço</span>
            </div>
          </Link>

          <div className="flex items-center gap-3">
            <Link 
              to="/auth" 
              className="text-xs sm:text-sm font-bold text-slate-600 hover:text-slate-900 transition-colors px-3 py-2 rounded-lg hover:bg-slate-100"
            >
              Entrar
            </Link>
            <Link 
              to="/auth?mode=register" 
              className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-xl transition-all shadow-md shadow-emerald-600/20 hover:scale-[1.02] active:scale-[0.98]"
            >
              <span>Testar 14 Dias Grátis</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section Contextual */}
      <section className="relative pt-10 pb-6 px-4 sm:px-6 max-w-4xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold mb-3 shadow-2xs">
          <Zap className="w-3.5 h-3.5 text-emerald-600" />
          <span>
            {activeTab === 'reforma' 
              ? 'Tecnologia 2026/2027: Simulador Oficial Reforma Tributária' 
              : activeTab === 'audit'
                ? 'Auditoria Financeira Instantânea de Lucro Real'
                : 'Fácil, Rápido e Sem Contas Complicadas'}
          </span>
        </div>

        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 tracking-tight leading-tight mb-3">
          {activeTab === 'reforma' ? (
            <>
              Como a Nova Reforma Tributária <span className="text-emerald-600">muda seu Preço de Venda?</span>
            </>
          ) : activeTab === 'audit' ? (
            <>
              Você está tendo lucro de verdade ou <span className="text-rose-600">pagando para trabalhar?</span>
            </>
          ) : (
            <>
              Por quanto devo vender meu produto para <span className="text-emerald-600">ter lucro de verdade?</span>
            </>
          )}
        </h1>

        <p className="text-slate-600 text-sm sm:text-base max-w-2xl mx-auto">
          {activeTab === 'reforma'
            ? 'Simule a transição do Simples para o IVA Dual (CBS + IBS). Veja créditos de compras, imposto por fora e o comparativo lado a lado antes vs. depois.'
            : activeTab === 'audit'
              ? 'Audite o preço que você já cobra hoje e descubra na hora se existe prejuízo invisível nas suas vendas.'
              : 'Responda a 3 perguntas simples e descubra na hora o preço ideal já blindando as taxas de maquininha e custos.'}
        </p>

        {/* Seletor Superior de Abas (Opção A) */}
        <div className="mt-7 inline-flex p-1.5 bg-slate-200/80 border border-slate-300/80 rounded-2xl max-w-2xl w-full shadow-inner">
          <button
            onClick={() => handleTabChange('quick')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl text-xs sm:text-sm font-bold transition-all ${
              activeTab === 'quick'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Calculator className="w-4 h-4 text-emerald-600" />
            <span>Preço Rápido</span>
          </button>

          <button
            onClick={() => handleTabChange('reforma')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl text-xs sm:text-sm font-bold transition-all relative ${
              activeTab === 'reforma'
                ? 'bg-emerald-600 text-white shadow-sm'
                : 'text-slate-700 hover:text-slate-900'
            }`}
          >
            <Zap className={`w-3.5 h-3.5 ${activeTab === 'reforma' ? 'text-amber-300 fill-amber-300' : 'text-emerald-600'}`} />
            <span>Simulador Reforma</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ml-1 ${
              activeTab === 'reforma' ? 'bg-white/20 text-white' : 'bg-emerald-100 text-emerald-800'
            }`}>
              CBS+IBS
            </span>
          </button>

          <button
            onClick={() => handleTabChange('audit')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl text-xs sm:text-sm font-bold transition-all ${
              activeTab === 'audit'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <TrendingUp className="w-4 h-4 text-indigo-600" />
            <span>Auditar Lucro</span>
          </button>
        </div>
      </section>

      {/* Main Container */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 pb-20 w-full">
        {/* ========================================================================= */}
        {/* ABA 1: PREÇO RÁPIDO (DIA A DIA - LIMPO, 3 PASSOS, SEM POLUIÇÃO) */}
        {/* ========================================================================= */}
        <div className={activeTab === 'quick' ? 'grid grid-cols-1 lg:grid-cols-12 gap-6 items-start' : 'hidden'} aria-hidden={activeTab !== 'quick'}>
          {/* Formulário Limpo */}
            <div className="lg:col-span-6 bg-white border border-slate-200 p-6 sm:p-7 rounded-3xl shadow-sm space-y-6">
              
              {/* Passo 1: Custo */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                    <span className="w-5 h-5 rounded-full bg-emerald-600 text-white flex items-center justify-center text-[11px] font-black">1</span>
                    Quanto custou para fazer ou comprar o item?
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setCostInput('30');
                      setProfitChoice(30);
                      setChannelChoice('credit');
                      setTaxChoice('mei');
                      setIncludeFixed(false);
                    }}
                    className="text-[11px] text-slate-400 hover:text-emerald-600 flex items-center gap-1 transition-colors font-semibold"
                  >
                    <RotateCcw className="w-3 h-3" />
                    <span>Exemplo</span>
                  </button>
                </div>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-sm">R$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={costInput}
                    onChange={(e) => setCostInput(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-300 rounded-xl pl-10 pr-4 py-3 text-slate-900 font-black text-lg focus:bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all outline-none"
                    placeholder="0,00"
                  />
                </div>
                <span className="text-[11px] text-slate-500 mt-1 block">
                  Ex: ingredientes, compra da peça, matéria-prima ou embalagem.
                </span>
              </div>

              {/* Passo 2: Margem Bruta / Sobra Unitária */}
              <div>
                <label className="text-xs font-bold text-slate-800 flex items-center justify-between gap-1.5 mb-1">
                  <div className="flex items-center gap-1.5">
                    <span className="w-5 h-5 rounded-full bg-emerald-600 text-white flex items-center justify-center text-[11px] font-black">2</span>
                    <span>Quanto você precisa sobrar por unidade para cobrir sua estrutura?</span>
                  </div>
                  <span className="text-[10px] text-slate-400 font-semibold">Margem Bruta</span>
                </label>
                <p className="text-[11px] text-slate-500 mb-2 leading-tight">
                  Essa sobra unitária — tecnicamente Margem Bruta — paga o aluguel, luz, funcionários e o seu pró-labore antes de qualquer lucro pessoal.
                </p>
                <div className="grid grid-cols-4 gap-2 mb-2">
                  {[15, 20, 30, 40].map((pct) => (
                    <button
                      key={pct}
                      type="button"
                      onClick={() => {
                        setProfitChoice(pct);
                        setCustomProfitInput(pct.toString());
                      }}
                      className={`py-2 px-1 text-xs font-bold rounded-xl border transition-all ${
                        profitChoice === pct
                          ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                          : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                      }`}
                    >
                      {pct}%
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setProfitChoice(0)}
                    className={`text-xs font-semibold px-2.5 py-1.5 rounded-lg border transition-all ${
                      profitChoice === 0 
                        ? 'bg-emerald-50 border-emerald-300 text-emerald-800' 
                        : 'border-transparent text-slate-500 hover:text-slate-800'
                    }`}
                  >
                    Outra porcentagem:
                  </button>
                  {profitChoice === 0 && (
                    <div className="relative w-28">
                      <input
                        type="number"
                        step="1"
                        min="1"
                        max="90"
                        value={customProfitInput}
                        onChange={(e) => setCustomProfitInput(e.target.value)}
                        className="w-full bg-white border border-emerald-400 rounded-lg px-2.5 py-1 text-xs font-bold text-slate-900 outline-none"
                        placeholder="Ex: 25"
                      />
                      <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-bold">%</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Passo 3: Canal de Venda */}
              <div>
                <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5 mb-2">
                  <span className="w-5 h-5 rounded-full bg-emerald-600 text-white flex items-center justify-center text-[11px] font-black">3</span>
                  Onde você costuma vender?
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {[
                    { id: 'pix', label: 'PIX / Dinheiro', fee: '0%' },
                    { id: 'debit', label: 'Cartão Débito', fee: '1,5%' },
                    { id: 'credit', label: 'Crédito à Vista', fee: '3,5%' },
                    { id: 'credit_split', label: 'Crédito Parc.', fee: '7,0%' },
                    { id: 'ifood', label: 'iFood / Delivery', fee: '18%' },
                    { id: 'mktplace', label: 'Shopee / M.Livre', fee: '14%' },
                  ].map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setChannelChoice(item.id)}
                      className={`p-2 rounded-xl border text-left transition-all ${
                        channelChoice === item.id
                          ? 'bg-emerald-50 border-emerald-500 text-emerald-950 font-bold shadow-2xs'
                          : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      <span className="block text-xs leading-tight">{item.label}</span>
                      <span className={`text-[10px] ${channelChoice === item.id ? 'text-emerald-700 font-bold' : 'text-slate-400'}`}>
                        Taxa: {item.fee}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Passo 4: Tipo de Empresa */}
              <div>
                <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5 mb-2">
                  <span className="w-5 h-5 rounded-full bg-slate-700 text-white flex items-center justify-center text-[11px] font-black">4</span>
                  Qual seu regime de imposto atual?
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setTaxChoice('mei')}
                    className={`p-2.5 rounded-xl border text-left transition-all ${
                      taxChoice === 'mei'
                        ? 'bg-emerald-50 border-emerald-500 text-emerald-950 font-bold shadow-2xs'
                        : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    <span className="block text-xs">Sou MEI</span>
                    <span className="text-[10px] text-slate-500">Sem imposto por venda (0%)</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setTaxChoice('simples')}
                    className={`p-2.5 rounded-xl border text-left transition-all ${
                      taxChoice === 'simples'
                        ? 'bg-emerald-50 border-emerald-500 text-emerald-950 font-bold shadow-2xs'
                        : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    <span className="block text-xs">Simples Nacional</span>
                    <span className="text-[10px] text-slate-500">Média de 6% sobre a nota</span>
                  </button>
                </div>
              </div>

              {/* Passo 5: Custos Fixos (Opcional) */}
              <div className="pt-2 border-t border-slate-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-slate-500" />
                    <div>
                      <span className="text-xs font-bold text-slate-800 block">
                        Tem aluguel ou contas fixas para pagar?
                      </span>
                      <span className="text-[11px] text-slate-500">
                        Opcional. Inclui uma fatia de aluguel/água/luz no preço.
                      </span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setIncludeFixed(!includeFixed)}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                      includeFixed ? 'bg-emerald-600' : 'bg-slate-300'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        includeFixed ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>

                {includeFixed && (
                  <div className="mt-3 p-3.5 bg-slate-50 border border-slate-200 rounded-2xl space-y-3">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-bold text-slate-700">Ajudante Automático:</span>
                      <span className="text-emerald-700 font-bold">Rateio estimado: {formatPct(activeFixedPct)}</span>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                          Gasto fixo do mês (R$)
                        </label>
                        <input
                          type="number"
                          value={monthlyFixedBills}
                          onChange={(e) => setMonthlyFixedBills(e.target.value)}
                          className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs font-bold text-slate-900 outline-none"
                          placeholder="Ex: 3000"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                          Quanto fatura no mês (R$)
                        </label>
                        <input
                          type="number"
                          value={monthlyRevenue}
                          onChange={(e) => setMonthlyRevenue(e.target.value)}
                          className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs font-bold text-slate-900 outline-none"
                          placeholder="Ex: 15000"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {idealCalc.hasError && (
                <div className="p-3.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-start gap-2.5">
                  <ShieldAlert className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
                  <span>{idealCalc.errorMsg}</span>
                </div>
              )}
            </div>

            {/* Painel do Preço Recomendado (Modo Rápido) */}
            <div className="lg:col-span-6 flex flex-col gap-5">
              <div className="bg-white border border-emerald-200 p-6 sm:p-7 rounded-3xl shadow-sm relative overflow-hidden">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs uppercase font-bold tracking-widest text-emerald-700">
                    Preço de Venda Recomendado
                  </span>
                  <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">
                    Markup: {idealCalc.comparison.currentPrice > 0 && idealCalc.rawCost > 0 ? (idealCalc.comparison.currentPrice / idealCalc.rawCost).toFixed(2) : '0.00'}x
                  </span>
                </div>

                <div className="text-4xl sm:text-5xl font-black text-slate-900 tracking-tight mb-2">
                  {formatBRL(idealCalc.comparison.currentPrice)}
                </div>

                <p className="text-xs text-slate-600 mb-5 leading-relaxed">
                  Cobrando esse valor, você cobre o custo da mercadoria, taxas do canal e impostos diretos, gerando uma sobra bruta de <strong className="text-emerald-800 font-bold">{formatBRL(idealCalc.comparison.currentProfitVal)} ({formatPct(idealCalc.profitPct)})</strong> por unidade para ajudar a pagar suas contas fixas e formar seu lucro.
                </p>

                {/* Resumo da Venda */}
                <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200 space-y-2.5 text-xs">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 pb-1 border-b border-slate-200">
                    Para onde vai cada real dessa venda?
                  </div>
                  <div className="flex justify-between text-slate-700 font-medium">
                    <span>Custo do produto/compra:</span>
                    <span className="font-bold text-slate-900">{formatBRL(idealCalc.rawCost)}</span>
                  </div>
                  <div className="flex justify-between text-slate-700 font-medium">
                    <span>Taxa da maquininha ({formatPct(idealCalc.feesPct)}):</span>
                    <span className="font-bold text-purple-700">-{formatBRL(idealCalc.feesVal)}</span>
                  </div>
                  {activeTaxPct > 0 && (
                    <div className="flex justify-between text-slate-700 font-medium">
                      <span>Impostos Simples ({formatPct(activeTaxPct)}):</span>
                      <span className="font-bold text-amber-700">-{formatBRL(idealCalc.comparison.currentTaxVal)}</span>
                    </div>
                  )}
                  {includeFixed && idealCalc.fixedVal > 0 && (
                    <div className="flex justify-between text-slate-700 font-medium">
                      <span>Fatia para contas fixas ({formatPct(idealCalc.fixedPct)}):</span>
                      <span className="font-bold text-slate-700">-{formatBRL(idealCalc.fixedVal)}</span>
                    </div>
                  )}
                  <div className="pt-2 border-t border-slate-200 flex justify-between text-sm font-black text-emerald-800">
                    <span>Sobra Bruta por Unidade:</span>
                    <span>+{formatBRL(idealCalc.comparison.currentProfitVal)} ({formatPct(idealCalc.profitPct)})</span>
                  </div>
                  <p className="text-[10px] text-slate-500 mt-1">
                    Atenção: essa sobra unitária precisa cobrir os custos fixos antes de gerar lucro líquido real.
                  </p>
                </div>

                {/* Banner de Convite para o Simulador da Reforma */}
                <div className="mt-5 p-4 bg-emerald-50/70 border border-emerald-200 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="space-y-0.5">
                    <span className="text-xs font-bold text-emerald-950 flex items-center gap-1.5">
                      <Zap className="w-3.5 h-3.5 text-emerald-600" />
                      Preparado para a Reforma Tributária 2026?
                    </span>
                    <p className="text-[11px] text-emerald-800/80">
                      Veja como o IVA Dual (CBS + IBS) e o crédito de insumos mudarão este preço.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleTabChange('reforma')}
                    className="shrink-0 text-xs font-bold bg-emerald-600 hover:bg-emerald-500 text-white px-3.5 py-2 rounded-xl transition-all shadow-xs flex items-center justify-center gap-1"
                  >
                    <span>Simular Reforma</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {/* Hub de Ações Pós-Cálculo (Hierarquia de CTAs) */}
              <PostCalculationActionHub
                tab="quick"
                unitPrice={idealCalc.comparison.currentPrice}
                unitContributionMargin={idealCalc.comparison.currentProfitVal}
                formatBRL={formatBRL}
                formatPct={formatPct}
                onOpenCatalogModal={() => handleOpenCatalogModal('quick')}
                onOpenTaxDiagnosticModal={() => handleOpenTaxDiagnosticModal('quick')}
              />
            </div>
        </div>

        {/* ========================================================================= */}
        {/* ABA 2: SIMULADOR REFORMA TRIBUTÁRIA (DEDICADO, DIDÁTICO E COMPARATIVO) */}
        {/* ========================================================================= */}
        <div className={activeTab === 'reforma' ? 'space-y-6' : 'hidden'} aria-hidden={activeTab !== 'reforma'}>
            {/* Banner Didático Topo */}
            <div className="p-4 sm:p-5 bg-emerald-50/80 border border-emerald-200 rounded-3xl flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-2xl bg-emerald-600 text-white flex items-center justify-center shrink-0 shadow-xs">
                  <Zap className="w-5 h-5 text-amber-300 fill-amber-300" />
                </div>
                <div>
                  <h2 className="text-sm sm:text-base font-black text-slate-900">
                    Simulador Oficial da Reforma Tributária (CBS + IBS — EC 132/2023)
                  </h2>
                  <p className="text-xs text-slate-600 mt-0.5 max-w-2xl">
                    Entenda como funcionam as 3 grandes mudanças: <strong>Cálculo do imposto "por fora"</strong>, <strong>Crédito de IVA na compra de insumos</strong> e o <strong>Super-poder competitivo na venda para empresas (B2B)</strong>.
                  </p>
                </div>
              </div>
              <div className="shrink-0 flex items-center gap-2">
                <span className="text-xs font-bold px-3 py-1 rounded-full bg-emerald-200 text-emerald-900">
                  Vigência 2026/2027
                </span>
              </div>
            </div>

            {/* Aviso Oficial TCU (28/10/2026 - TC 006.529/2026-7) */}
            <div className="p-4 bg-slate-900 border border-slate-800 rounded-3xl text-slate-200 space-y-2 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2 pb-2 border-b border-slate-800">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                  <span className="text-xs font-bold text-white uppercase tracking-wider">
                    Atualização Oficial TCU (28/10/2026) — Processo TC 006.529/2026-7
                  </span>
                </div>
                <span className="text-[10px] font-mono bg-slate-800 text-emerald-400 px-2.5 py-0.5 rounded-full border border-slate-700">
                  LC 214/2025 art. 349 • Resolução-TCU nº 388/2026
                </span>
              </div>
              <p className="text-xs text-slate-300 leading-relaxed">
                O Plenário do Tribunal de Contas da União calculou a alíquota de referência técnica da <strong>CBS em 11,3%</strong> (exato 11,3468%) e o <strong>redutor para compras governamentais em 44,2%</strong>. A matéria tramita no Senado Federal para fixação definitiva até 15/12.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
              {/* Formulário da Reforma */}
              <div className="lg:col-span-6 bg-white border border-slate-200 p-6 sm:p-7 rounded-3xl shadow-sm space-y-6">
                
                {/* 1. SELEÇÃO DE ATIVIDADE, CLIENTE E REGIME EMPRESARIAL */}
                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-3.5">
                  <span className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                    <Layers className="w-4 h-4 text-emerald-600" />
                    Enquadramento da Operação na Reforma
                  </span>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* O que você vende? */}
                    <div>
                      <label className="block text-[11px] font-bold text-slate-700 mb-1.5">
                        1. O que você vende?
                      </label>
                      <div className="grid grid-cols-2 gap-1.5">
                        <button
                          type="button"
                          onClick={() => setActivityType('product')}
                          className={`py-2 px-2 rounded-xl text-xs font-bold transition-all border flex items-center justify-center gap-1.5 ${
                            activityType === 'product'
                              ? 'bg-emerald-600 border-emerald-600 text-white shadow-xs'
                              : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'
                          }`}
                        >
                          <Package className="w-3.5 h-3.5" />
                          <span>Produto</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setActivityType('service')}
                          className={`py-2 px-2 rounded-xl text-xs font-bold transition-all border flex items-center justify-center gap-1.5 ${
                            activityType === 'service'
                              ? 'bg-emerald-600 border-emerald-600 text-white shadow-xs'
                              : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'
                          }`}
                        >
                          <Wrench className="w-3.5 h-3.5" />
                          <span>Serviço</span>
                        </button>
                      </div>
                    </div>

                    {/* Quem é o cliente? */}
                    <div>
                      <label className="block text-[11px] font-bold text-slate-700 mb-1.5">
                        2. Quem é o seu cliente?
                      </label>
                      <div className="grid grid-cols-3 gap-1.5">
                        <button
                          type="button"
                          onClick={() => setCustomerType('b2c')}
                          className={`py-2 px-1.5 rounded-xl text-xs font-bold transition-all border flex items-center justify-center gap-1 ${
                            customerType === 'b2c'
                              ? 'bg-slate-900 border-slate-900 text-white shadow-xs'
                              : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'
                          }`}
                        >
                          <Users className="w-3 h-3 shrink-0" />
                          <span className="text-[11px]">B2C (Geral)</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setCustomerType('b2b')}
                          className={`py-2 px-1.5 rounded-xl text-xs font-bold transition-all border flex items-center justify-center gap-1 ${
                            customerType === 'b2b'
                              ? 'bg-indigo-700 border-indigo-700 text-white shadow-xs'
                              : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'
                          }`}
                        >
                          <Briefcase className="w-3 h-3 shrink-0" />
                          <span className="text-[11px]">B2B (Empresa)</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setCustomerType('governo')}
                          className={`py-2 px-1.5 rounded-xl text-xs font-bold transition-all border flex items-center justify-center gap-1 ${
                            customerType === 'governo'
                              ? 'bg-amber-600 border-amber-600 text-white shadow-xs'
                              : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'
                          }`}
                        >
                          <Building2 className="w-3 h-3 shrink-0" />
                          <span className="text-[11px]">Gov (-44,2%)</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Fonte da Alíquota de Referência (Artigo 2.5) */}
                  <div className="pt-2 border-t border-slate-200">
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="block text-[11px] font-bold text-slate-700">
                        Fonte da Alíquota de Referência:
                      </label>
                      <span className="text-[10px] text-slate-500 font-semibold">Artigo 2.5 Constituição R-MAX</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setTaxBenchmark('tcu_2026')}
                        className={`p-2 rounded-xl border text-left transition-all ${
                          taxBenchmark === 'tcu_2026'
                            ? 'bg-emerald-50 border-emerald-500 text-emerald-950 font-bold shadow-2xs'
                            : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        <span className="block text-xs leading-tight flex items-center gap-1 font-bold">
                          <Zap className="w-3 h-3 text-emerald-600 shrink-0" />
                          TCU Plenário (CBS 11,3%)
                        </span>
                        <span className="text-[10px] text-emerald-700 font-semibold block mt-0.5">
                          Oficial TC 006.529/2026-7 (Teto 28,0%)
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setTaxBenchmark('fazenda_preliminar_8_8')}
                        className={`p-2 rounded-xl border text-left transition-all ${
                          taxBenchmark === 'fazenda_preliminar_8_8'
                            ? 'bg-emerald-50 border-emerald-500 text-emerald-950 font-bold shadow-2xs'
                            : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        <span className="block text-xs leading-tight font-bold">
                          Min. Fazenda (CBS 8,8%)
                        </span>
                        <span className="text-[10px] text-slate-500 block mt-0.5">
                          Projeção Preliminar EC 132/2023 (Teto 26,5%)
                        </span>
                      </button>
                    </div>
                  </div>

                  {/* 3. Regime Tributário da sua Empresa na Reforma */}
                  <div className="pt-2 border-t border-slate-200">
                    <label className="block text-[11px] font-bold text-slate-700 mb-1.5">
                      3. Regime da sua Empresa na Reforma:
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5">
                      <button
                        type="button"
                        onClick={() => setCompanyTaxRegime('simples_hibrido')}
                        className={`p-2.5 rounded-xl border text-left transition-all ${
                          companyTaxRegime === 'simples_hibrido'
                            ? 'bg-emerald-50 border-emerald-500 text-emerald-950 font-bold shadow-2xs'
                            : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        <span className="block text-xs leading-tight flex items-center gap-1">
                          <Zap className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                          Simples Híbrido
                        </span>
                        <span className="text-[10px] text-emerald-700 font-semibold block mt-0.5">
                          Regime Regular CBS/IBS (Recupera Crédito)
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setCompanyTaxRegime('simples_normal')}
                        className={`p-2.5 rounded-xl border text-left transition-all ${
                          companyTaxRegime === 'simples_normal'
                            ? 'bg-slate-900 border-slate-900 text-white font-bold shadow-2xs'
                            : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        <span className="block text-xs leading-tight">Simples Normal</span>
                        <span className={`text-[10px] block mt-0.5 ${companyTaxRegime === 'simples_normal' ? 'text-slate-300' : 'text-slate-400'}`}>
                          Paga DAS único (Sem crédito de compras)
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setCompanyTaxRegime('lucro_real_presumido')}
                        className={`p-2.5 rounded-xl border text-left transition-all ${
                          companyTaxRegime === 'lucro_real_presumido'
                            ? 'bg-indigo-700 border-indigo-700 text-white font-bold shadow-2xs'
                            : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        <span className="block text-xs leading-tight">Lucro Real / Pres.</span>
                        <span className={`text-[10px] block mt-0.5 ${companyTaxRegime === 'lucro_real_presumido' ? 'text-indigo-200' : 'text-slate-400'}`}>
                          Débito e crédito pleno (Não-cumulativo)
                        </span>
                      </button>
                    </div>
                  </div>

                  {/* Subcategoria se for Serviço */}
                  {activityType === 'service' && (
                    <div className="pt-3 border-t border-slate-200">
                      <label className="block text-[11px] font-bold text-slate-700 mb-1.5">
                        Tipo de Serviço (Alíquotas com Desconto na Lei):
                      </label>
                      <div className="grid grid-cols-3 gap-1.5">
                        <button
                          type="button"
                          onClick={() => setServiceCategory('general')}
                          className={`p-2 rounded-xl border text-left transition-all ${
                            serviceCategory === 'general'
                              ? 'bg-emerald-50 border-emerald-500 text-emerald-950 font-bold'
                              : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'
                          }`}
                        >
                          <span className="block text-xs leading-tight">Serviço Geral</span>
                          <span className="text-[10px] text-slate-400">26,5% IVA</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setServiceCategory('health_edu')}
                          className={`p-2 rounded-xl border text-left transition-all ${
                            serviceCategory === 'health_edu'
                              ? 'bg-emerald-50 border-emerald-500 text-emerald-950 font-bold'
                              : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'
                          }`}
                        >
                          <span className="block text-xs leading-tight">Saúde / Educação</span>
                          <span className="text-[10px] text-emerald-600 font-bold">-60% (10,6%)</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setServiceCategory('liberal_pro')}
                          className={`p-2 rounded-xl border text-left transition-all ${
                            serviceCategory === 'liberal_pro'
                              ? 'bg-emerald-50 border-emerald-500 text-emerald-950 font-bold'
                              : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'
                          }`}
                        >
                          <span className="block text-xs leading-tight">Prof. Liberal</span>
                          <span className="text-[10px] text-emerald-600 font-bold">-30% (18,5%)</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* 2. CUSTO DO ITEM E ANATOMIA DA COMPRA */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                      <span className="w-5 h-5 rounded-full bg-emerald-600 text-white flex items-center justify-center text-[11px] font-black">1</span>
                      Quanto custou para fazer ou comprar o item?
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        setCostInput('100');
                        setProfitChoice(30);
                        setChannelChoice('credit');
                      }}
                      className="text-[11px] text-slate-400 hover:text-emerald-600 flex items-center gap-1 transition-colors font-semibold"
                    >
                      <RotateCcw className="w-3 h-3" />
                      <span>Exemplo (R$ 100,00)</span>
                    </button>
                  </div>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-sm">R$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={costInput}
                      onChange={(e) => setCostInput(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-300 rounded-xl pl-10 pr-4 py-3 text-slate-900 font-black text-lg focus:bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all outline-none"
                      placeholder="0,00"
                    />
                  </div>

                  {/* Detalhamento de Custos para Serviços (Assimetria Mão de Obra vs Insumos) */}
                  {activityType === 'service' && (
                    <div className="mt-3.5 space-y-3">
                      <div className="p-3.5 bg-amber-50/80 border border-amber-200 rounded-2xl text-xs space-y-1">
                        <span className="font-bold text-amber-950 flex items-center gap-1.5 text-[11px] uppercase tracking-wider">
                          <Zap className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                          Lei da Assimetria Tributária (Serviços)
                        </span>
                        <p className="text-[11px] text-amber-900 leading-relaxed">
                          A folha de pagamento, pró-labore e encargos <strong>NÃO geram crédito de CBS/IBS</strong>. Apenas insumos físicos e peças aplicadas com nota fiscal geram créditos dedutíveis.
                        </p>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[11px] font-bold text-slate-700 mb-1">
                            Mão de Obra / Valor Hora (R$):
                          </label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs">R$</span>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={serviceLaborInput}
                              onChange={(e) => setServiceLaborInput(e.target.value)}
                              className="w-full bg-slate-50 border border-slate-300 rounded-xl pl-9 pr-3 py-2 text-slate-900 font-bold text-sm focus:bg-white focus:ring-2 focus:ring-emerald-500 outline-none"
                              placeholder="0,00"
                            />
                          </div>
                          <span className="text-[10px] text-slate-500 block mt-0.5">Sem crédito fiscal de IVA</span>
                        </div>
                        <div>
                          <label className="block text-[11px] font-bold text-slate-700 mb-1">
                            Insumos Físicos / Peças (R$):
                          </label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs">R$</span>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={serviceMaterialInput}
                              onChange={(e) => setServiceMaterialInput(e.target.value)}
                              className="w-full bg-slate-50 border border-slate-300 rounded-xl pl-9 pr-3 py-2 text-slate-900 font-bold text-sm focus:bg-white focus:ring-2 focus:ring-emerald-500 outline-none"
                              placeholder="0,00"
                            />
                          </div>
                          <span className="text-[10px] text-emerald-700 font-semibold block mt-0.5">Recupera créditos de CBS/IBS</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Detalhe de Compra e Anatomia do Custo no Estoque para Produtos */}
                  {activityType === 'product' ? (
                    <div className="mt-3.5 space-y-3">
                      {/* Seletor do Cenário do Fornecedor */}
                      <div>
                        <span className="block text-[11px] font-bold text-slate-700 mb-1.5">
                          Como o fornecedor calcula o preço de venda dele?
                        </span>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() => setPurchaseScenario('full_rate')}
                            className={`p-2.5 rounded-xl border text-left transition-all ${
                              purchaseScenario === 'full_rate'
                                ? 'bg-emerald-50 border-emerald-500 text-emerald-950 font-bold shadow-2xs'
                                : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'
                            }`}
                          >
                            <span className="block text-xs font-bold leading-tight">Alíquota Cheia (Pós-Reforma)</span>
                            <span className="text-[10px] text-slate-500 block mt-0.5">
                              Base líquida R$ {idealCalc.rawCost > 0 ? idealCalc.rawCost.toFixed(2) : '100,00'} + CBS 8,8% + IBS 17,7% por fora
                            </span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setPurchaseScenario('transition_legacy')}
                            className={`p-2.5 rounded-xl border text-left transition-all ${
                              purchaseScenario === 'transition_legacy'
                                ? 'bg-emerald-50 border-emerald-500 text-emerald-950 font-bold shadow-2xs'
                                : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'
                            }`}
                          >
                            <span className="block text-xs font-bold leading-tight">Transição da Compra Atual</span>
                            <span className="text-[10px] text-slate-500 block mt-0.5">
                              NF de hoje desonera 27,25% antigos e soma CBS/IBS
                            </span>
                          </button>
                        </div>
                      </div>

                      {/* Card de Anatomia da Compra & Estoque (Alinhado com a Planilha do Ronny) */}
                      <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-2xl space-y-2 text-xs">
                        <div className="flex items-center justify-between pb-1.5 border-b border-slate-200 text-[11px] font-bold uppercase tracking-wider text-slate-600">
                          <span>Anatomia da Compra & Entrada no Estoque</span>
                          <span className={`text-[10px] px-2 py-0.5 rounded-md font-bold ${
                            companyTaxRegime === 'simples_normal' 
                              ? 'bg-amber-100 text-amber-900 border border-amber-200' 
                              : 'bg-emerald-100 text-emerald-900 border border-emerald-200'
                          }`}>
                            {companyTaxRegime === 'simples_normal' ? 'Simples Normal' : 'Regime com Crédito'}
                          </span>
                        </div>
                        
                        <div className="flex justify-between text-slate-600 text-[11px]">
                          <span>Base líquida da mercadoria (sem impostos):</span>
                          <span className="font-semibold text-slate-800">{formatBRL(idealCalc.supplierNetBase)}</span>
                        </div>
                        <div className="flex justify-between text-slate-600 text-[11px]">
                          <span>(+) CBS destacada pelo fornecedor (8,8%):</span>
                          <span className="font-semibold text-amber-700">+{formatBRL(idealCalc.purchaseCbsVal)}</span>
                        </div>
                        <div className="flex justify-between text-slate-600 text-[11px]">
                          <span>(+) IBS destacado pelo fornecedor (17,7%):</span>
                          <span className="font-semibold text-amber-700">+{formatBRL(idealCalc.purchaseIbsVal)}</span>
                        </div>
                        <div className="flex justify-between text-slate-900 font-bold text-xs pt-1 border-t border-slate-200">
                          <span>Total desembolsado na NF de Compra:</span>
                          <span>{formatBRL(idealCalc.purchaseNfTotal)}</span>
                        </div>
                        
                        <div className="flex justify-between text-xs pt-1 border-t border-slate-200">
                          <span className="text-slate-600">(-) Crédito tributário recuperado para o caixa:</span>
                          {idealCalc.totalPurchaseCredits > 0 ? (
                            <span className="font-bold text-emerald-700">-{formatBRL(idealCalc.totalPurchaseCredits)}</span>
                          ) : (
                            <span className="font-bold text-rose-600">R$ 0,00 (Não apropria no Simples Normal)</span>
                          )}
                        </div>

                        <div className="p-2.5 bg-white border border-emerald-300 rounded-xl flex items-center justify-between mt-1 shadow-2xs">
                          <div>
                            <span className="block text-[11px] font-black text-slate-900 uppercase tracking-tight">
                              Custo Líquido Real no Estoque (CMV):
                            </span>
                            <span className="text-[10px] text-slate-500">
                              Base contábil real sobre a qual incide sua margem de lucro
                            </span>
                          </div>
                          <span className="text-base font-black text-emerald-700">
                            {formatBRL(idealCalc.inventoryNetCost)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-2.5 p-2.5 bg-blue-50/60 border border-blue-100 rounded-xl text-[11px] text-blue-900 flex items-start gap-2">
                      <Info className="w-3.5 h-3.5 text-blue-600 shrink-0 mt-0.5" />
                      <span>
                        <strong>Nota sobre Serviços:</strong> Serviços têm baixo crédito na compra de insumos porque a maior despesa é mão de obra. O tributo incide sobre o valor faturado com as reduções legais de alíquota.
                      </span>
                    </div>
                  )}
                </div>

                {/* 3. MARGEM BRUTA / SOBRA UNITÁRIA */}
                <div>
                  <label className="text-xs font-bold text-slate-800 flex items-center justify-between gap-1.5 mb-1">
                    <div className="flex items-center gap-1.5">
                      <span className="w-5 h-5 rounded-full bg-emerald-600 text-white flex items-center justify-center text-[11px] font-black">2</span>
                      <span>Quanto você precisa sobrar por unidade após os novos tributos?</span>
                    </div>
                    <span className="text-[10px] text-slate-400 font-semibold">Margem Bruta</span>
                  </label>
                  <p className="text-[11px] text-slate-500 mb-2 leading-tight">
                    Calculada por fora na CBS/IBS. Protege a empresa contra o aumento do custo dos insumos e perdas de crédito fiscal.
                  </p>
                  <div className="grid grid-cols-4 gap-2 mb-2">
                    {[15, 20, 30, 40].map((pct) => (
                      <button
                        key={pct}
                        type="button"
                        onClick={() => {
                          setProfitChoice(pct);
                          setCustomProfitInput(pct.toString());
                        }}
                        className={`py-2 px-1 text-xs font-bold rounded-xl border transition-all ${
                          profitChoice === pct
                            ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                            : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                        }`}
                      >
                        {pct}%
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setProfitChoice(0)}
                      className={`text-xs font-semibold px-2.5 py-1.5 rounded-lg border transition-all ${
                        profitChoice === 0 
                          ? 'bg-emerald-50 border-emerald-300 text-emerald-800' 
                          : 'border-transparent text-slate-500 hover:text-slate-800'
                      }`}
                    >
                      Outra porcentagem:
                    </button>
                    {profitChoice === 0 && (
                      <div className="relative w-28">
                        <input
                          type="number"
                          step="1"
                          min="1"
                          max="90"
                          value={customProfitInput}
                          onChange={(e) => setCustomProfitInput(e.target.value)}
                          className="w-full bg-white border border-emerald-400 rounded-lg px-2.5 py-1 text-xs font-bold text-slate-900 outline-none"
                          placeholder="Ex: 25"
                        />
                        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-bold">%</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* 4. CANAL DE VENDA */}
                <div>
                  <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5 mb-2">
                    <span className="w-5 h-5 rounded-full bg-emerald-600 text-white flex items-center justify-center text-[11px] font-black">3</span>
                    Canal de Venda / Meio de Recebimento
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {[
                      { id: 'pix', label: 'PIX / Boleto', fee: '0%' },
                      { id: 'debit', label: 'Débito', fee: '1,5%' },
                      { id: 'credit', label: 'Crédito à Vista', fee: '3,5%' },
                      { id: 'credit_split', label: 'Crédito Parc.', fee: '7,0%' },
                      { id: 'ifood', label: 'iFood / Delivery', fee: '18%' },
                      { id: 'mktplace', label: 'Marketplace PJ', fee: '14%' },
                    ].map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => setChannelChoice(item.id)}
                        className={`p-2 rounded-xl border text-left transition-all ${
                          channelChoice === item.id
                            ? 'bg-emerald-50 border-emerald-500 text-emerald-950 font-bold shadow-2xs'
                            : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100'
                        }`}
                      >
                        <span className="block text-xs leading-tight">{item.label}</span>
                        <span className={`text-[10px] ${channelChoice === item.id ? 'text-emerald-700 font-bold' : 'text-slate-400'}`}>
                          Taxa: {item.fee}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* 5. ALÍQUOTA CBS + IBS NA REFORMA */}
                <div>
                  <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5 mb-2">
                    <span className="w-5 h-5 rounded-full bg-slate-700 text-white flex items-center justify-center text-[11px] font-black">4</span>
                    Alíquota CBS + IBS Aplicada (Calculada Por Fora)
                  </label>
                  <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-slate-800">
                        {companyTaxRegime === 'simples_normal'
                          ? 'Simples Nacional Normal (DAS):'
                          : activityType === 'service' && serviceCategory === 'health_edu' 
                            ? 'Saúde & Educação (-60% de Redução):' 
                            : activityType === 'service' && serviceCategory === 'liberal_pro'
                              ? 'Profissionais Regulamentados (-30% de Redução):'
                              : 'Alíquota CBS + IBS de Referência:'}
                      </span>
                      <span className="text-xs font-black text-emerald-700 bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-200">
                        {formatPct(idealCalc.taxPct)} {companyTaxRegime === 'simples_normal' ? '(Via DAS)' : '(Por Fora)'}
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-500 leading-tight">
                      {companyTaxRegime === 'simples_normal'
                        ? 'Empresas no Simples Normal recolhem pelo DAS unificado sobre o faturamento.'
                        : 'No regime regular, o imposto é somado por fora sobre o preço líquido da empresa, sem incidir sobre ele mesmo.'}
                    </p>
                  </div>
                </div>

                {idealCalc.hasError && (
                  <div className="p-3.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-start gap-2.5">
                    <ShieldAlert className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
                    <span>{idealCalc.errorMsg}</span>
                  </div>
                )}
              </div>

              {/* Coluna Direita: Resultado da Reforma + B2B + Comparativo Solicitado */}
              <div className="lg:col-span-6 flex flex-col gap-5">
                
                {/* Card 1: Preço Recomendado com IVA na Reforma */}
                <div className="bg-white border border-emerald-200 p-6 sm:p-7 rounded-3xl shadow-sm relative overflow-hidden">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs uppercase font-bold tracking-widest text-emerald-700 flex items-center gap-1.5">
                      <Zap className="w-3.5 h-3.5 text-emerald-600" />
                      Preço Final na Reforma (CBS + IBS)
                    </span>
                    <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">
                      Markup: {idealCalc.markupMultiplier.toFixed(2)}x
                    </span>
                  </div>

                  <div className="text-4xl sm:text-5xl font-black text-slate-900 tracking-tight mb-2">
                    {formatBRL(idealCalc.comparison.reformaPrice)}
                  </div>

                  <p className="text-xs text-slate-600 mb-5 leading-relaxed">
                    Preço total na nota fiscal. Descontando as taxas e o imposto, sobram exatamente <strong className="text-emerald-700 font-bold">{formatBRL(idealCalc.profitVal)} ({formatPct(idealCalc.profitPct)})</strong> limpos para o seu negócio.
                  </p>

                  {/* Detalhamento do Preço */}
                  <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200 space-y-2 text-xs">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 pb-1 border-b border-slate-200">
                      Detalhamento do Preço "Por Fora":
                    </div>
                    <div className="flex justify-between text-slate-700 font-medium">
                      <span>Custo líquido de estoque (CMV Real):</span>
                      <span className="font-bold text-slate-900">{formatBRL(idealCalc.inventoryNetCost)}</span>
                    </div>
                    {idealCalc.feesVal > 0 && (
                      <div className="flex justify-between text-slate-700 font-medium">
                        <span>Taxa de pagamento ({formatPct(idealCalc.feesPct)}):</span>
                        <span className="font-bold text-purple-700">-{formatBRL(idealCalc.feesVal)}</span>
                      </div>
                    )}
                    {includeFixed && idealCalc.fixedVal > 0 && (
                      <div className="flex justify-between text-slate-700 font-medium">
                        <span>Fatia para custos fixos ({formatPct(idealCalc.fixedPct)}):</span>
                        <span className="font-bold text-slate-700">-{formatBRL(idealCalc.fixedVal)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-slate-700 font-medium">
                      <span>Subtotal da Empresa (Preço Líquido):</span>
                      <span className="font-bold text-slate-900">{formatBRL(idealCalc.netSellingPrice)}</span>
                    </div>
                    <div className="flex justify-between text-slate-700 font-medium">
                      <span>Imposto destacado na nota ({formatPct(idealCalc.taxPct)} por fora):</span>
                      <span className="font-bold text-amber-700">+{formatBRL(idealCalc.taxVal)}</span>
                    </div>

                    <div className="pt-2 border-t border-slate-200 flex justify-between text-sm font-black text-emerald-700">
                      <span>Sobram no seu bolso:</span>
                      <span>+{formatBRL(idealCalc.profitVal)} ({formatPct(idealCalc.profitPct)})</span>
                    </div>
                  </div>

                  {/* Card de Apuração Fiscal (O detalhe que várias empresas fazem na venda) */}
                  <div className="mt-3.5 p-3.5 bg-emerald-50/80 border border-emerald-200 rounded-2xl space-y-1.5 text-xs">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-950 flex items-center gap-1.5">
                      <Zap className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                      Apuração Fiscal (O que você realmente recolhe ao governo)
                    </span>
                    {companyTaxRegime !== 'simples_normal' ? (
                      <div className="space-y-1 pt-1 text-[11px]">
                        <div className="flex justify-between text-slate-700">
                          <span>Débito gerado na venda (CBS + IBS na nota de saída):</span>
                          <span className="font-semibold text-slate-900">+{formatBRL(idealCalc.taxVal)}</span>
                        </div>
                        <div className="flex justify-between text-slate-700">
                          <span>(-) Crédito aproveitado da compra (insumos/mercadorias):</span>
                          <span className="font-semibold text-emerald-700">-{formatBRL(idealCalc.totalPurchaseCredits)}</span>
                        </div>
                        <div className="flex justify-between font-bold text-xs pt-1 border-t border-emerald-200 text-emerald-950">
                          <span>Imposto Efetivo Líquido a Pagar:</span>
                          <span className="font-black text-emerald-800">{formatBRL(idealCalc.reformaNetTaxToPay)}</span>
                        </div>
                        <p className="text-[10px] text-slate-500 pt-0.5 leading-tight">
                          💡 <strong>Princípio da Não-Cumulatividade:</strong> Você recolhe imposto apenas sobre o valor que sua empresa agregou ao produto ({formatBRL(Math.max(0, idealCalc.netSellingPrice - idealCalc.inventoryNetCost))}).
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-1 pt-1 text-[11px]">
                        <div className="flex justify-between font-bold text-xs text-slate-900">
                          <span>Guia DAS Simples Nacional a Recolher:</span>
                          <span className="font-black text-slate-900">{formatBRL(idealCalc.reformaNetTaxToPay)}</span>
                        </div>
                        <p className="text-[10px] text-amber-800 leading-tight">
                          ⚠️ No Simples Normal, o imposto incide sobre a receita bruta total, sem direito ao abatimento dos créditos de compra dos fornecedores.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Card 2: Destaque B2B (Se o cliente for empresa) */}
                {customerType === 'b2b' && (
                  <div className="p-5 bg-indigo-50/70 border border-indigo-200 rounded-3xl space-y-3">
                    <div className="flex items-center justify-between text-xs font-bold text-indigo-950">
                      <span className="flex items-center gap-1.5">
                        <Briefcase className="w-4 h-4 text-indigo-700" />
                        Visão do seu Cliente Empresa (B2B):
                      </span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                        companyTaxRegime !== 'simples_normal' 
                          ? 'bg-indigo-200 text-indigo-900' 
                          : 'bg-amber-200 text-amber-900'
                      }`}>
                        {companyTaxRegime !== 'simples_normal' ? 'Crédito Integral' : 'Sem Crédito Pleno'}
                      </span>
                    </div>

                    {companyTaxRegime !== 'simples_normal' ? (
                      <>
                        <div className="grid grid-cols-2 gap-2 text-xs pt-1">
                          <div className="p-3 bg-white rounded-2xl border border-indigo-100">
                            <span className="text-[10px] text-slate-500 block">Preço Total na Nota</span>
                            <span className="font-black text-slate-800 text-sm">{formatBRL(idealCalc.comparison.reformaPrice)}</span>
                          </div>
                          <div className="p-3 bg-white rounded-2xl border border-indigo-100">
                            <span className="text-[10px] text-emerald-600 font-semibold block">Crédito que seu cliente PJ abate</span>
                            <span className="font-black text-emerald-700 text-sm">-{formatBRL(idealCalc.b2bCreditTransferred)}</span>
                          </div>
                        </div>
                        <div className="p-3 bg-indigo-950 text-white rounded-2xl flex items-center justify-between text-xs">
                          <span className="font-semibold">Custo Real Desembolsado pelo Cliente PJ:</span>
                          <span className="font-black text-base text-amber-300">{formatBRL(idealCalc.b2bEffectiveCostForClient)}</span>
                        </div>
                        <p className="text-[11px] text-indigo-900/80 leading-tight">
                          💡 <strong>Dica Comercial:</strong> Mostre ao comprador da empresa que o custo real dele é de apenas {formatBRL(idealCalc.b2bEffectiveCostForClient)}, pois ele recupera o imposto da sua nota como crédito para pagar menos tributo no negócio dele.
                        </p>
                      </>
                    ) : (
                      <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-2xl space-y-1.5 text-xs text-amber-950">
                        <span className="font-bold block">Atenção à Competitividade B2B:</span>
                        <p className="text-[11px] leading-relaxed text-amber-900">
                          No Simples Normal, sua nota fiscal não destaca 26,5% de crédito de CBS/IBS. O comprador corporativo não consegue abater esse valor e pode preferir comprar de empresas no <strong>Simples Híbrido</strong> ou <strong>Lucro Real</strong> para aproveitar os créditos tributários.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Card 2.1: Compras Governamentais (Redutor TCU 44,2%) */}
                {customerType === 'governo' && (
                  <div className="p-5 bg-amber-50/80 border border-amber-200 rounded-3xl space-y-3">
                    <div className="flex items-center justify-between text-xs font-bold text-amber-950">
                      <span className="flex items-center gap-1.5">
                        <Building2 className="w-4 h-4 text-amber-700" />
                        Compras Governamentais (Órgãos Públicos):
                      </span>
                      <span className="text-[10px] px-2.5 py-0.5 rounded-full font-bold bg-amber-200 text-amber-950 border border-amber-300">
                        Redutor TCU 44,2% Aplicado
                      </span>
                    </div>
                    <div className="p-3.5 bg-white rounded-2xl border border-amber-200 space-y-1 text-xs">
                      <div className="flex justify-between text-slate-600 text-[11px]">
                        <span>Alíquota de Referência Base:</span>
                        <span className="font-semibold text-slate-800">{idealCalc.cbsRate + idealCalc.ibsRate}%</span>
                      </div>
                      <div className="flex justify-between text-emerald-700 text-[11px] font-bold">
                        <span>(-) Redutor Legal para Compras Públicas:</span>
                        <span>-44,2%</span>
                      </div>
                      <div className="flex justify-between text-slate-900 font-bold text-xs pt-1 border-t border-slate-100">
                        <span>Alíquota Efetiva da Venda Governamental:</span>
                        <span className="text-amber-800 font-black">{formatPct(idealCalc.taxPct)}</span>
                      </div>
                    </div>
                    <p className="text-[11px] text-amber-900 leading-relaxed">
                      🏛️ <strong>Fundamentação Legal:</strong> Fixado pelo Acórdão do Plenário do TCU (Processo TC 006.529/2026-7), regulamentado pelo art. 349 da LC 214/2025 e Resolução-TCU nº 388/2026 para aquisições pela administração pública direta, autarquias e fundações.
                    </p>
                  </div>
                )}

                {/* Card 3: COMPARATIVO OFICIAL DOS 4 CENÁRIOS (MATRIZ 4SHIFT) - OCULTO TEMPORARIAMENTE CONFORME SOLICITAÇÃO */}
                {false && (
                <div className="bg-white border border-slate-200 rounded-3xl p-5 sm:p-6 shadow-xs space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center font-bold shrink-0">
                        <Zap className="w-4 h-4 text-emerald-600" />
                      </div>
                      <div>
                        <h3 className="text-sm font-black text-slate-900 leading-tight">
                          Comparativo Oficial da Compra (Hoje vs. Reforma)
                        </h3>
                        <p className="text-[11px] text-slate-500">
                          Mesmo item com valor base de {formatBRL(idealCalc.rawCost > 0 ? idealCalc.rawCost : 100)} analisado nos 4 regimes contábeis
                        </p>
                      </div>
                    </div>
                    <a
                      href="https://www.gestao4shift.com.br/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-blue-50 hover:bg-blue-100 border border-blue-200 text-[#0062a8] text-[11px] font-bold transition-all shadow-xs group shrink-0"
                      title="Acessar Gestão 4Shift (gestao4shift.com.br)"
                    >
                      <img 
                        src={logo4Shift} 
                        alt="Logo 4Shift" 
                        className="h-4 w-auto object-contain rounded-xs" 
                      />
                      <span className="text-blue-900 group-hover:text-[#0062a8]">Matriz 4Shift</span>
                      <ExternalLink className="w-3.5 h-3.5 text-blue-600 group-hover:translate-x-0.5 transition-transform" />
                    </a>
                  </div>

                  {/* Tabela dos 4 Cenários */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead>
                        <tr className="border-b border-slate-200 bg-slate-50 text-[11px] font-bold text-slate-700">
                          <th className="py-2.5 px-3">Item / Métrica</th>
                          <th className="py-2.5 px-2 text-right">Lucro Real Hoje</th>
                          <th className="py-2.5 px-2 text-right bg-amber-50/60 text-amber-950">Simples Normal</th>
                          <th className="py-2.5 px-2 text-right bg-emerald-50/80 text-emerald-950">Simples Híbrido</th>
                          <th className="py-2.5 px-2 text-right">Lucro Real Pós-Ref.</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 text-[11px]">
                        <tr>
                          <td className="py-2 px-3 font-semibold text-slate-700">Apropria créditos IBS/CBS?</td>
                          <td className="py-2 px-2 text-right font-bold text-emerald-700">Sim (27,25% hoje)</td>
                          <td className="py-2 px-2 text-right font-bold text-rose-600 bg-amber-50/30">Não (0%)</td>
                          <td className="py-2 px-2 text-right font-bold text-emerald-700 bg-emerald-50/40">Sim (100%)</td>
                          <td className="py-2 px-2 text-right font-bold text-emerald-700">Sim (100%)</td>
                        </tr>
                        <tr>
                          <td className="py-2 px-3 text-slate-600">Base líquida do fornecedor</td>
                          <td className="py-2 px-2 text-right">{formatBRL(idealCalc.ronnyComparative.lucroRealHoje.baseLiquida)}</td>
                          <td className="py-2 px-2 text-right bg-amber-50/30">{formatBRL(idealCalc.ronnyComparative.simplesNormal.baseLiquida)}</td>
                          <td className="py-2 px-2 text-right bg-emerald-50/40">{formatBRL(idealCalc.ronnyComparative.simplesHibrido.baseLiquida)}</td>
                          <td className="py-2 px-2 text-right">{formatBRL(idealCalc.ronnyComparative.lucroRealPosReforma.baseLiquida)}</td>
                        </tr>
                        <tr>
                          <td className="py-2 px-3 text-slate-600">(+) CBS destacada (8,8%)</td>
                          <td className="py-2 px-2 text-right text-slate-400">-</td>
                          <td className="py-2 px-2 text-right bg-amber-50/30">+{formatBRL(idealCalc.ronnyComparative.simplesNormal.cbs)}</td>
                          <td className="py-2 px-2 text-right bg-emerald-50/40">+{formatBRL(idealCalc.ronnyComparative.simplesHibrido.cbs)}</td>
                          <td className="py-2 px-2 text-right">+{formatBRL(idealCalc.ronnyComparative.lucroRealPosReforma.cbs)}</td>
                        </tr>
                        <tr>
                          <td className="py-2 px-3 text-slate-600">(+) IBS destacado (17,7%)</td>
                          <td className="py-2 px-2 text-right text-slate-400">-</td>
                          <td className="py-2 px-2 text-right bg-amber-50/30">+{formatBRL(idealCalc.ronnyComparative.simplesNormal.ibs)}</td>
                          <td className="py-2 px-2 text-right bg-emerald-50/40">+{formatBRL(idealCalc.ronnyComparative.simplesHibrido.ibs)}</td>
                          <td className="py-2 px-2 text-right">+{formatBRL(idealCalc.ronnyComparative.lucroRealPosReforma.ibs)}</td>
                        </tr>
                        <tr className="font-bold bg-slate-50/70 text-slate-900">
                          <td className="py-2 px-3">Total da NF de compra</td>
                          <td className="py-2 px-2 text-right">{formatBRL(idealCalc.ronnyComparative.lucroRealHoje.totalNf)}</td>
                          <td className="py-2 px-2 text-right bg-amber-100/50 text-amber-950">{formatBRL(idealCalc.ronnyComparative.simplesNormal.totalNf)}</td>
                          <td className="py-2 px-2 text-right bg-emerald-100/50 text-emerald-950">{formatBRL(idealCalc.ronnyComparative.simplesHibrido.totalNf)}</td>
                          <td className="py-2 px-2 text-right text-indigo-950">{formatBRL(idealCalc.ronnyComparative.lucroRealPosReforma.totalNf)}</td>
                        </tr>
                        <tr>
                          <td className="py-2 px-3 text-slate-600">Total de créditos recuperados</td>
                          <td className="py-2 px-2 text-right text-emerald-700">+{formatBRL(idealCalc.ronnyComparative.lucroRealHoje.creditos)}</td>
                          <td className="py-2 px-2 text-right text-rose-600 font-bold bg-amber-50/30">R$ 0,00</td>
                          <td className="py-2 px-2 text-right text-emerald-700 font-bold bg-emerald-50/40">+{formatBRL(idealCalc.ronnyComparative.simplesHibrido.creditos)}</td>
                          <td className="py-2 px-2 text-right text-emerald-700 font-bold">+{formatBRL(idealCalc.ronnyComparative.lucroRealPosReforma.creditos)}</td>
                        </tr>
                        <tr className="font-black text-xs border-t-2 border-slate-300">
                          <td className="py-2.5 px-3 text-slate-900">CUSTO COM REFORMA (Estoque)</td>
                          <td className="py-2.5 px-2 text-right text-slate-900">{formatBRL(idealCalc.ronnyComparative.lucroRealHoje.custoEstoque)}</td>
                          <td className="py-2.5 px-2 text-right text-rose-700 bg-amber-100/70 font-black">{formatBRL(idealCalc.ronnyComparative.simplesNormal.custoEstoque)}</td>
                          <td className="py-2.5 px-2 text-right text-emerald-700 bg-emerald-100/70 font-black">{formatBRL(idealCalc.ronnyComparative.simplesHibrido.custoEstoque)}</td>
                          <td className="py-2.5 px-2 text-right text-indigo-900 font-black">{formatBRL(idealCalc.ronnyComparative.lucroRealPosReforma.custoEstoque)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Resumo Explicativo em Linguagem Clara (Público 40/50+) */}
                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-[11px] text-slate-600 leading-relaxed space-y-1">
                    <strong className="text-slate-800 block">Conclusão Estratégica da Matriz:</strong>
                    <ul className="space-y-1 list-disc list-inside">
                      <li>
                        No <strong>Simples Híbrido</strong> o custo real no estoque fica protegido em <strong>{formatBRL(idealCalc.ronnyComparative.simplesHibrido.custoEstoque)}</strong> (igual ao Lucro Real), porque a empresa apropria o crédito integral da CBS e do IBS.
                      </li>
                      <li>
                        No <strong>Simples Normal</strong> o custo do estoque sobe para <strong>{formatBRL(idealCalc.ronnyComparative.simplesNormal.custoEstoque)}</strong> porque a empresa perde o direito de creditar os impostos pagos na compra.
                      </li>
                    </ul>
                  </div>
                </div>
                )}

                {/* Hub de Ações Pós-Cálculo (Hierarquia de CTAs) */}
                <PostCalculationActionHub
                  tab="reforma"
                  unitPrice={idealCalc.comparison.reformaPrice}
                  unitContributionMargin={idealCalc.comparison.reformaProfitVal}
                  formatBRL={formatBRL}
                  formatPct={formatPct}
                  onOpenCatalogModal={() => handleOpenCatalogModal('reforma')}
                  onOpenTaxDiagnosticModal={() => handleOpenTaxDiagnosticModal('reforma')}
                />

              </div>
            </div>
        </div>

        {/* ========================================================================= */}
        {/* ABA 3: AUDITORIA RÁPIDA DE PREÇO ATUAL ("VER SE ESTOU LUCRANDO") */}
        {/* ========================================================================= */}
        <div className={activeTab === 'audit' ? 'grid grid-cols-1 lg:grid-cols-12 gap-6 items-start' : 'hidden'} aria-hidden={activeTab !== 'audit'}>
          <div className="lg:col-span-6 bg-white border border-slate-200 p-6 sm:p-7 rounded-3xl shadow-sm space-y-5">
              <div>
                <h2 className="text-base font-bold text-slate-900 mb-1">Quanto você cobra hoje?</h2>
                <p className="text-xs text-slate-500">Veja se você está realmente lucrando ou pagando para trabalhar.</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1.5">
                    Custo do Produto (R$)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs">R$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={auditCost}
                      onChange={(e) => setAuditCost(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-300 rounded-xl pl-9 pr-3 py-2.5 text-slate-900 font-bold text-sm outline-none focus:bg-white focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-900 mb-1.5">
                    Preço que você vende (R$)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-700 font-bold text-xs">R$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={auditPrice}
                      onChange={(e) => setAuditPrice(e.target.value)}
                      className="w-full bg-emerald-50/50 border border-emerald-300 rounded-xl pl-9 pr-3 py-2.5 text-emerald-900 font-bold text-sm outline-none focus:bg-white focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Como você recebe essa venda?
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: 'pix', label: 'PIX (0%)' },
                    { id: 'credit', label: 'Crédito (3,5%)' },
                    { id: 'ifood', label: 'iFood (18%)' },
                  ].map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setAuditChannel(item.id)}
                      className={`p-2 rounded-xl border text-xs font-bold transition-all ${
                        auditChannel === item.id
                          ? 'bg-indigo-50 border-indigo-500 text-indigo-900'
                          : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Regime:
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setAuditTaxChoice('mei')}
                    className={`p-2 rounded-xl border text-xs font-bold transition-all ${
                      auditTaxChoice === 'mei'
                        ? 'bg-indigo-50 border-indigo-500 text-indigo-900'
                        : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    MEI (0%)
                  </button>
                  <button
                    type="button"
                    onClick={() => setAuditTaxChoice('simples')}
                    className={`p-2 rounded-xl border text-xs font-bold transition-all ${
                      auditTaxChoice === 'simples'
                        ? 'bg-indigo-50 border-indigo-500 text-indigo-900'
                        : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    Simples Nacional (6%)
                  </button>
                </div>
              </div>
            </div>

            {/* Diagnóstico */}
            <div className="lg:col-span-6 flex flex-col gap-6">
              <div className={`p-6 sm:p-7 rounded-3xl border shadow-sm transition-all ${
                auditCalc.isLoss 
                  ? 'bg-rose-50/60 border-rose-300' 
                  : auditCalc.netMarginPct < 15 
                    ? 'bg-amber-50/60 border-amber-300' 
                    : 'bg-emerald-50/60 border-emerald-300'
              }`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs uppercase font-bold tracking-widest text-slate-600">Diagnóstico de Lucro</span>
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                    auditCalc.isLoss ? 'bg-rose-100 text-rose-800' : 'bg-emerald-100 text-emerald-800'
                  }`}>
                    {auditCalc.isLoss ? 'PREJUÍZO DISFARÇADO' : 'LUCRO POSITIVO'}
                  </span>
                </div>

                <div className={`text-4xl sm:text-5xl font-black tracking-tight mb-2 ${
                  auditCalc.isLoss ? 'text-rose-700' : 'text-emerald-700'
                }`}>
                  {formatBRL(auditCalc.netProfit)}
                  <span className="text-lg sm:text-xl font-medium text-slate-600 ml-2">
                    ({formatPct(auditCalc.netMarginPct)})
                  </span>
                </div>

                <p className="text-xs text-slate-600 mb-6 leading-relaxed">
                  {auditCalc.isLoss ? (
                    <strong className="text-rose-700">Atenção: você está pagando para trabalhar neste produto! Cada venda gera perda financeira.</strong>
                  ) : auditCalc.netMarginPct < 15 ? (
                    <span>Sua margem está no limite ({formatPct(auditCalc.netMarginPct)}). Qualquer aumento do fornecedor te joga no prejuízo.</span>
                  ) : (
                    <span>Parabéns! Sua margem de {formatPct(auditCalc.netMarginPct)} é saudável e sustenta seu negócio.</span>
                  )}
                </p>

                <div className="bg-white rounded-2xl p-4 border border-slate-200 space-y-2 text-xs">
                  <div className="flex justify-between text-slate-700 font-medium">
                    <span>Preço de Venda:</span>
                    <span className="font-bold text-slate-900">{formatBRL(auditCalc.price)}</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span>(-) Custo da Mercadoria:</span>
                    <span className="text-slate-800 font-semibold">{formatBRL(auditCalc.cost)}</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span>(-) Taxa do Canal:</span>
                    <span className="text-purple-700 font-semibold">-{formatBRL(auditCalc.feeVal)}</span>
                  </div>
                  {auditCalc.taxVal > 0 && (
                    <div className="flex justify-between text-slate-600">
                      <span>(-) Impostos:</span>
                      <span className="text-amber-700 font-semibold">-{formatBRL(auditCalc.taxVal)}</span>
                    </div>
                  )}
                  <div className={`pt-2 border-t border-slate-200 flex justify-between text-sm font-black ${
                    auditCalc.isLoss ? 'text-rose-700' : 'text-emerald-800'
                  }`}>
                    <span>Sobra Bruta por Unidade:</span>
                    <span>{formatBRL(auditCalc.netProfit)} ({formatPct(auditCalc.netMarginPct)})</span>
                  </div>
                  <p className="text-[10px] text-slate-500 mt-1">
                    Essa sobra unitária precisa cobrir despesas fixas (aluguel, folha e pró-labore) antes de formar lucro líquido.
                  </p>
                </div>
              </div>

              {/* Hub de Ações Pós-Cálculo (Hierarquia de CTAs) */}
              <PostCalculationActionHub
                tab="audit"
                unitPrice={auditCalc.price}
                unitContributionMargin={auditCalc.netProfit}
                formatBRL={formatBRL}
                formatPct={formatPct}
                onOpenCatalogModal={() => handleOpenCatalogModal('audit')}
                onOpenTaxDiagnosticModal={() => handleOpenTaxDiagnosticModal('audit')}
              />
            </div>
        </div>

        {/* Guia SEO Educativo e Artigo explicativo (FAQ) */}
        <section className="mt-16 pt-12 border-t border-slate-200">
          <div className="max-w-3xl mx-auto space-y-10">
            <div className="text-center">
              <h2 className="text-2xl sm:text-3xl font-black text-slate-900 mb-3">
                Dúvidas Comuns sobre Preço de Venda e Reforma
              </h2>
              <p className="text-sm text-slate-600">
                Tudo o que você precisa saber para precificar seus produtos sem perder dinheiro.
              </p>
            </div>

            {/* Perguntas Frequentes (FAQ) */}
            <div className="space-y-3">
              {FAQS_DATA.map((item, idx) => (
                <div 
                  key={idx} 
                  className="rounded-2xl bg-white border border-slate-200 overflow-hidden transition-all shadow-2xs"
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    aria-expanded={openFaq === idx}
                    className="w-full text-left p-4 sm:p-5 flex items-center justify-between gap-4 font-bold text-sm text-slate-900 hover:text-emerald-700 transition-colors"
                  >
                    <span>{item.q}</span>
                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${openFaq === idx ? 'rotate-180 text-emerald-600' : ''}`} />
                  </button>
                  <div 
                    className={`px-4 sm:px-5 pb-4 pt-1 text-xs text-slate-600 leading-relaxed border-t border-slate-100 ${
                      openFaq === idx ? 'block' : 'hidden'
                    }`}
                  >
                    {item.a}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      {/* Footer Simples em Tema Claro */}
      <footer className="border-t border-slate-200 bg-white py-8 mt-auto text-center text-xs text-slate-500">
        <div className="max-w-5xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p>© {new Date().getFullYear()} RESOLV MAX. Todos os direitos reservados.</p>
          <div className="flex items-center gap-4 font-medium">
            <Link to="/" className="hover:text-emerald-700 transition-colors">Página Inicial</Link>
            <Link to="/planos" className="hover:text-emerald-700 transition-colors">Planos</Link>
            <Link to="/termos" className="hover:text-emerald-700 transition-colors">Termos</Link>
            <Link to="/privacidade" className="hover:text-emerald-700 transition-colors">Privacidade</Link>
          </div>
        </div>
      </footer>

      {/* Modais de Captação B2B e Diagnóstico Tributário */}
      <CatalogAuditLeadModal
        isOpen={isCatalogModalOpen}
        onClose={() => setIsCatalogModalOpen(false)}
        originTab={catalogModalOrigin}
      />
      <TaxDiagnosticIntroModal
        isOpen={isTaxDiagnosticModalOpen}
        onClose={() => setIsTaxDiagnosticModalOpen(false)}
        originTab={catalogModalOrigin}
      />
    </div>
  );
}
